// ============================================================
// fondos/efectos/manchas.js — lo que comparten los efectos de CAPAS (módulo PURO)
//
// P3 del motor de fondos, fase F3 (`CLAUDE_RESUMEN_PLANES/2026-09-24_plan-p3-motor-efectos.md`
// §2.4; desde el 30-09, §3.1-bis, solo aurora: humo, mesh y blobs volvieron a lienzo y fluido se
// retiró). Los 5 fluidos (aurora, smoke, gradient-flow, mesh-gradient y blobs-morph) dejan de ser un
// lienzo a ¼ de resolución repintado 30 veces por segundo y pasan a MANCHAS: `div` con un
// `radial-gradient` de borde suave, animados con la Web Animations API solo en `transform` y
// `opacity`, que corren en el compositor (0 % de hilo principal: Aurora se comía el 22,6 %) y
// quedan nítidos a cualquier DPR.
//
// Un efecto de capas no toca el DOM: DESCRIBE sus manchas (`disenar(entorno)`) y la base
// (`fondos/capas.js`) las monta y gobierna sus animaciones. Así el contrato se prueba en node.
//
// LAS MEDIDAS VAN EN UNIDADES DE CONTENEDOR (`cqmax`, `cqw`, `cqh`: la capa es un contenedor de
// tamaño). Medido en Chrome 153 (29-09-2026): unas animaciones WAAPI con `cqh` en los fotogramas
// corren en el compositor (0,16 % de hilo, frente al 7,6 % animando `top`) y se re-resuelven solas
// cuando la sección cambia de alto. Por eso una mancha sigue redonda aunque la sección crezca (E6),
// y el humo sube la sección entera tenga el alto que tenga, sin JS al redimensionar.
//
// DOS TOPES DE MEMORIA, porque cada mancha animada es una capa del compositor:
//   · el tamaño de cada caja lleva un tope en px (`min(Ncqmax, Mpx)`), y la suma de las cajas de
//     un fondo no pasa de `AREA_MAX` px CSS: a DPR 2, 4 bytes por píxel, 24 MB (el umbral del plan);
//   · ninguna escala de un fotograma pasa de 1: Chrome rasteriza una capa con la transformación
//     animada a la ESCALA MÁXIMA de su animación, así que crecer ×1,5 costaría ×2,25 de memoria.
//     Una mancha que crece se dibuja ya a su tamaño mayor y arranca encogida.
//
// MÓDULO PURO: ES2019 sin `?.` ni `??`, sin DOM y sin estado de módulo.
// ============================================================

/** DPR al que se mide el tope de memoria (el de `fondos/lienzo.js::DPR_MAX`). */
export const DPR_TOPE = 2;

/** Memoria de las capas de UN fondo, en bytes: el umbral técnico del plan (§2.4). */
export const MEMORIA_MAX = 24 * 1024 * 1024;

/** Área total (px CSS) que pueden sumar las cajas de las manchas de un fondo. */
export const AREA_MAX = Math.floor(MEMORIA_MAX / (4 * DPR_TOPE * DPR_TOPE));

/**
 * Cuánto dura un ciclo con la velocidad `speed` (1-10, la del catálogo): `base` a velocidad 5, unas
 * ×2,4 más lento a 1 y unas ×3 más rápido a 10. Sin velocidad en el dato, la 5.
 */
export function duracion(base, speed) {
    const v = (typeof speed === 'number' && speed >= 1 && speed <= 10) ? speed : 5;
    return Math.round(base * Math.pow(2, (5 - v) / 3.2));
}

/**
 * Un parámetro 1-10 del catálogo interpolado y redondeado a un entero entre `min` y `max` (densidad →
 * número de manchas). Sin valor en el dato, `defecto`.
 */
export function cuantas(valor, defecto, min, max) {
    const v = (typeof valor === 'number' && valor >= 1 && valor <= 10) ? valor : defecto;
    return Math.round(min + (v - 1) * (max - min) / 9);
}

/**
 * El lado en px CSS que puede tener, como mucho, cada una de `n` cajas cuadradas para que su área
 * sume `AREA_MAX` (redondeado hacia abajo).
 */
export function ladoMax(n) {
    return Math.floor(Math.sqrt(AREA_MAX / Math.max(1, n)));
}

/** `min(<cq>cqmax, <px>px)`: una medida de contenedor con su tope. */
export function medida(cq, px) {
    return 'min(' + cq + 'cqmax, ' + Math.floor(px) + 'px)';
}

/**
 * El PERFIL del degradado de una mancha: 11 paradas `[radio 0-1, alfa]` de `alfa` en el centro a 0 en
 * el borde, con pendiente nula al llegar (sin filo). La SUAVIDAD (la clave `blur` del dato, 0-100,
 * E10) la interpreta el efecto aquí: más suavidad = núcleo más pequeño y cola más larga, sin
 * `filter` (dejaba viñeta en los bordes y costaba fps).
 *
 * @param {number} alfa - alfa del núcleo (0-1)
 * @param {number} [suavidad] - 0-100
 * @param {number} [dureza] - exponente base: <2 = mancha con cuerpo (blobs), >2 = bruma
 */
export function perfil(alfa, suavidad, dureza) {
    const s = (typeof suavidad === 'number' && suavidad > 0) ? Math.min(suavidad, 100) / 100 : 0;
    const p = (typeof dureza === 'number' ? dureza : 1.8) + s * 2.4;
    const out = [];
    for (let i = 0; i <= 10; i++) {
        const r = i / 10;
        out.push([r, i === 10 ? 0 : Math.round(alfa * Math.pow(1 - r * r, p) * 10000) / 10000]);
    }
    return out;
}

/**
 * Una mancha que crece con la suavidad: el núcleo encoge con ella, y la mancha se agranda un poco
 * para no perder presencia (hasta ×1,2). Devuelve el factor de tamaño.
 */
export function crecePorSuavidad(suavidad) {
    const s = (typeof suavidad === 'number' && suavidad > 0) ? Math.min(suavidad, 100) / 100 : 0;
    return 1 + 0.2 * s;
}

/** Un número entre `a` y `b` con el azar del entorno (redondeado a 2 decimales: CSS legible). */
export function entre(azar, a, b) {
    return Math.round((a + (b - a) * azar()) * 100) / 100;
}

/**
 * Los fotogramas de un DESPLAZAMIENTO lento en torno a la casilla de la mancha: `n` puntos al azar
 * dentro de ±`dx` cqw y ±`dy` cqh, con una escala entre `escalaMin` y 1 (nunca más: ver cabecera).
 * El primero es la casilla. Pensado para `direction: alternate`.
 */
export function deriva(azar, n, dx, dy, escalaMin, giro) {
    const out = [];
    for (let i = 0; i < n; i++) {
        const x = i === 0 ? 0 : entre(azar, -dx, dx);
        const y = i === 0 ? 0 : entre(azar, -dy, dy);
        const e = i === 0 ? 1 : entre(azar, escalaMin, 1);
        const g = giro ? (i === 0 ? 0 : entre(azar, -giro, giro)) : null;
        out.push({ transform: 'translate(' + x + 'cqw, ' + y + 'cqh)' + (g !== null ? ' rotate(' + g + 'deg)' : '') + ' scale(' + e + ')' });
    }
    return out;
}

/**
 * Los fotogramas de una ONDA: un bucle cerrado en `n` pasos (pensado para `direction: normal` y curva
 * lineal) en el que la pieza sube y baja `ay` cqh, va y vuelve `ax` cqw (una elipse, como una partícula
 * de agua), se inclina hasta `giro` grados siguiendo la pendiente y respira su opacidad entre `opMin` y
 * 1. Varias piezas con la misma onda y el retraso escalonado forman una cinta por la que la onda VIAJA
 * (aurora); el signo de `giro` va con el sentido del escalonado.
 */
export function onda(n, ay, ax, giro, opMin) {
    const out = [];
    for (let i = 0; i <= n; i++) {
        const a = (i / n) * Math.PI * 2;
        const x = Math.round(ax * Math.cos(a) * 100) / 100;
        const y = Math.round(ay * Math.sin(a) * 100) / 100;
        const g = Math.round(giro * Math.cos(a) * 100) / 100;
        const o = Math.round((opMin + (1 - opMin) * (0.5 - 0.5 * Math.sin(a))) * 1000) / 1000;
        out.push({ transform: 'translate(' + x + 'cqw, ' + y + 'cqh) rotate(' + g + 'deg)', opacity: o });
    }
    return out;
}

/**
 * Los fotogramas de una ÓRBITA elíptica de radios `rx` cqw y `ry` cqh, en `n` pasos, empezando en
 * `fase` (radianes) y en el sentido `sentido` (1 | -1). Cierra en el punto de partida: pensada para
 * `direction: normal` y curva lineal (una vuelta continua).
 */
export function orbita(rx, ry, n, fase, sentido, escalaMin) {
    const out = [];
    for (let i = 0; i <= n; i++) {
        const a = fase + sentido * (i / n) * Math.PI * 2;
        const x = Math.round(rx * Math.cos(a) * 100) / 100;
        const y = Math.round(ry * Math.sin(a) * 100) / 100;
        // Respira dos veces por vuelta, sin pasar de 1.
        const e = Math.round((1 - (1 - escalaMin) * (0.5 - 0.5 * Math.cos(2 * (a - fase)))) * 1000) / 1000;
        out.push({ transform: 'translate(' + x + 'cqw, ' + y + 'cqh) scale(' + e + ')' });
    }
    return out;
}
